import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DesignGallery = ({ designs }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  const currentDesign = designs?.[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? designs?.length - 1 : prev - 1));
    setIsZoomed(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === designs?.length - 1 ? 0 : prev + 1));
    setIsZoomed(false);
  };

  const handleImageClick = (e) => {
    if (currentDesign?.type === 'image') {
      const rect = e?.target?.getBoundingClientRect();
      const x = ((e?.clientX - rect?.left) / rect?.width) * 100;
      const y = ((e?.clientY - rect?.top) / rect?.height) * 100;
      setZoomPosition({ x, y });
      setIsZoomed(!isZoomed);
    }
  };

  const renderDesignContent = () => {
    switch (currentDesign?.type) {
      case 'image':
        return (
          <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden cursor-zoom-in">
            <Image
              src={currentDesign?.url}
              alt={currentDesign?.title}
              className={`w-full h-full object-contain transition-transform duration-300 ${
                isZoomed ? 'scale-200 cursor-zoom-out' : 'cursor-zoom-in'
              }`}
              style={isZoomed ? {
                transformOrigin: `${zoomPosition?.x}% ${zoomPosition?.y}%`
              } : {}}
              onClick={handleImageClick}
            />
            {isZoomed && (
              <div className="absolute top-4 right-4 z-10">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsZoomed(false)}
                  iconName="ZoomOut"
                  iconPosition="left"
                >
                  Reset Zoom
                </Button>
              </div>
            )}
          </div>
        );
      
      case 'video':
        return (
          <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden">
            <video
              src={currentDesign?.url}
              controls
              className="w-full h-full object-contain"
              poster={currentDesign?.thumbnail}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      
      case 'cad':
        return (
          <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden flex items-center justify-center">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto">
                <Icon name="Box" size={32} color="var(--color-accent)" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-foreground">{currentDesign?.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">CAD Drawing File</p>
              </div>
              <Button
                variant="outline"
                onClick={() => window.open(currentDesign?.url, '_blank')}
                iconName="Download"
                iconPosition="left"
              >
                View CAD File
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Design Display */}
      <div className="relative">
        <div className="aspect-[4/3] w-full max-h-[600px] bg-card rounded-lg border border-border overflow-hidden elevation-2">
          {renderDesignContent()}
        </div>

        {/* Navigation Controls */}
        {designs?.length > 1 && (
          <>
            <Button
              variant="secondary"
              size="icon"
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full elevation-2"
            >
              <Icon name="ChevronLeft" size={20} />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full elevation-2"
            >
              <Icon name="ChevronRight" size={20} />
            </Button>
          </>
        )}

        {/* Design Counter */}
        {designs?.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full border border-border">
            <span className="text-sm font-medium text-foreground">
              {currentIndex + 1} of {designs?.length}
            </span>
          </div>
        )}
      </div>
      {/* Design Information */}
      <div className="bg-card rounded-lg border border-border p-6 elevation-1">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h2 className="text-xl font-heading text-foreground">{currentDesign?.title}</h2>
            <p className="text-muted-foreground">{currentDesign?.description}</p>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={14} />
                <span>Created {currentDesign?.createdAt}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="FileType" size={14} />
                <span className="capitalize">{currentDesign?.type}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full"></div>
            <span className="text-sm text-success font-medium">Secure</span>
          </div>
        </div>
      </div>
      {/* Thumbnail Navigation */}
      {designs?.length > 1 && (
        <div className="flex space-x-3 overflow-x-auto pb-2">
          {designs?.map((design, index) => (
            <button
              key={design?.id}
              onClick={() => {
                setCurrentIndex(index);
                setIsZoomed(false);
              }}
              className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden transition-luxury ${
                index === currentIndex
                  ? 'border-accent' :'border-border hover:border-accent/50'
              }`}
            >
              {design?.type === 'image' ? (
                <Image
                  src={design?.thumbnail || design?.url}
                  alt={design?.title}
                  className="w-full h-full object-cover"
                />
              ) : design?.type === 'video' ? (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Icon name="Play" size={16} color="var(--color-muted-foreground)" />
                </div>
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Icon name="Box" size={16} color="var(--color-muted-foreground)" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesignGallery;